import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Step 6. What is offered immediately after this product is added. The shared
// default is right nearly always, so it is presented as the default.
const Upsell = ({ product, choices, commit }) => {
  const chosen = product.upsell_steps || [];
  const override = !!product.upsell_override;
  const labelFor = (key) => (choices.upsell_steps.find((x) => x.key === key) || {}).label || key;
  // Two kinds of set, one join table underneath: add-ons (a checkbox in the chain)
  // and personalised Printess items (a gallery). Empty means every set of that kind,
  // which is what the store does with no rows (#5423, #5328).
  //
  // Each kind belongs to ONE step of the chain -- StoreGallery resolves the
  // retouching step from `addon_product_ids` and the personalised step from
  // `printess_product_ids` -- so the picker is rendered under its own step rather
  // than in a section of its own. "Personalized gifts" is not one product: there may
  // be a holiday set and a fall set, and which one this product offers is a
  // different question from whether the step runs at all.
  const setKinds = [
    {
      key: 'addon_product_ids',
      step: 'retouching',
      title: 'Which add-ons are offered',
      noun: 'add-on',
      sets: choices.addon_sets || [],
      chosen: product.addon_product_ids || [],
    },
    {
      key: 'printess_product_ids',
      step: 'personalized',
      title: 'Which personalised items are offered',
      noun: 'gift',
      sets: choices.printess_sets || [],
      chosen: product.printess_product_ids || [],
    },
  ];
  const kindForStep = (key) => setKinds.find((k) => k.step === key && k.sets.length);

  const save = (nextOverride, nextSteps) => commit({
    override: nextOverride ? '1' : '0',
    upsell_steps: nextSteps.map(String),
  });

  // Sent on its own so a change to the sets cannot be read as a change to the chain,
  // and vice versa -- the server only touches the links when the key is present.
  // BOTH kinds go every time: the server replaces the whole list, so a kind left out
  // of the request would have to be inferred rather than stated.
  const saveSets = (key, ids) => commit({
    override: override ? '1' : '0',
    upsell_steps: chosen.map(String),
    ...Object.fromEntries(setKinds.map((k) => [
      k.key, (k.key === key ? ids : k.chosen).map(String),
    ])),
  });

  return (
    <Stack spacing={1}>
      <RadioGroup value={override ? 'own' : 'shared'}>
        <FormControlLabel
          value="shared"
          control={<Radio onChange={() => save(false, [])} />}
          sx={{ alignItems: 'flex-start', m: 0 }}
          label={(
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Use the usual sequence</Typography>
              <Typography variant="caption" color="text.secondary">
                {choices.inherited_upsell?.length
                  ? `Currently: ${choices.inherited_upsell.map(labelFor).join(' → ')}. `
                  : 'Currently nothing is offered after adding to the cart. '}
                Managed for all products under Upsell flow.
              </Typography>
            </Box>
          )}
        />
        <FormControlLabel
          value="own"
          control={<Radio onChange={() => save(true, chosen)} />}
          sx={{ alignItems: 'flex-start', m: 0, mt: 1 }}
          label={(
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Something different for this product</Typography>
              <Typography variant="caption" color="text.secondary">
                Pick what to offer. Pick nothing to go straight to the cart — right for a one-off
                item like a graduation card.
              </Typography>
            </Box>
          )}
        />
      </RadioGroup>

      {/* display: flex, NOT block. FormControlLabel is inline-flex; forcing it to
          block stops the checkbox and the label being flex items, and the label is a
          Typography -- which renders a <p> -- so it dropped onto its own line under
          the box. flex keeps each row full width AND the pair side by side. */}
      <Box sx={{ pl: 4 }}>
        {choices.upsell_steps.map((step) => {
          const kind = kindForStep(step.key);
          const on = chosen.includes(step.key);

          return (
            <Box key={step.key}>
              <FormControlLabel
                sx={{ display: 'flex' }}
                control={(
                  <Checkbox
                    disabled={!override}
                    checked={on}
                    onChange={(e) => save(true, e.target.checked
                      ? [...chosen, step.key]
                      : chosen.filter((k) => k !== step.key))}
                  />
                )}
                label={<Typography variant="body2">{step.label}</Typography>}
              />
              {/* Only once the step is actually running: choosing which gifts to
                  offer on a step that never opens is a setting with no effect. */}
              {kind && on ? (
                <Box sx={{ pl: 4, pb: 1 }}>
                  <TextField
                    select
                    size="small"
                    label={`Which ${kind.noun}s`}
                    sx={{ minWidth: 280 }}
                    value={kind.chosen}
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (ids) => (ids.length
                        ? kind.sets.filter((set) => ids.includes(set.id)).map((set) => set.name).join(', ')
                        : `Every ${kind.noun} (${kind.sets.length})`),
                    }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => saveSets(kind.key, e.target.value)}
                    helperText={kind.chosen.length
                      ? 'Only these, in this order.'
                      : `Nothing picked, so all ${kind.sets.length} are offered.`}
                  >
                    {kind.sets.map((set) => (
                      <MenuItem key={set.id} value={set.id}>
                        <Checkbox checked={kind.chosen.includes(set.id)} size="small" />
                        {set.name}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          {` · ${set.items} item${set.items === 1 ? '' : 's'}`}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

Upsell.propTypes = stepPropTypes;
Upsell.defaultProps = stepDefaultProps;

export default Upsell;
